'use client'

import { forwardRef, type CSSProperties, type InputHTMLAttributes, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn, type IconType } from '../utils'
import '../../styles/form-layout.css'
import '../../styles/search-layout.css'

export type ControlSize = 'sm' | 'md' | 'lg' | 'field'
export type ControlShape = 'control' | 'content'
export type ControlVariant = 'default' | 'plain'

interface ControlAppearance {
  controlSize?: ControlSize
  shape?: ControlShape
  variant?: ControlVariant
}

const heights: Record<ControlSize, string> = { sm: 'h-control-sm', md: 'h-control', lg: 'h-control-lg', field: 'h-[var(--ui-form-control-height,46px)]' }
const textSizes: Record<ControlSize, string> = { sm: 'text-small', md: 'text-body', lg: 'text-body', field: 'text-body' }

/** One chrome owner for labelled fields, toolbar filters and unlabelled native controls.
 * Labels and descriptions belong to the field/composite; these elements retain native form APIs. */
function controlClass({ controlSize = 'md', shape = 'control', variant = 'default' }: ControlAppearance, invalid: boolean) {
  return cn(
    'w-full min-w-0 border text-ink placeholder:text-ink-muted',
    shape === 'content' ? 'rounded-tile' : 'rounded-control',
    textSizes[controlSize],
    '[@media(pointer:coarse)]:text-base transition-[border-color,box-shadow] duration-fast',
    'disabled:cursor-not-allowed disabled:text-ink-muted',
    variant === 'plain'
      ? 'bg-transparent'
      : cn('bg-surface hover:border-line-strong focus-visible:border-accent disabled:bg-surface-2', shape === 'control' && 'shadow-control'),
    invalid ? 'border-tone-danger-fg hover:border-tone-danger-fg' : variant === 'default' ? 'border-line-strong/60' : 'border-transparent',
  )
}

const suffixInset: CSSProperties & { '--ui-form-control-inset-end': string } = { '--ui-form-control-inset-end': '3.5rem' }

const isInvalid = (value: InputHTMLAttributes<HTMLInputElement>['aria-invalid']) => value === true || value === 'true' || value === 'grammar' || value === 'spelling'

export interface InputControlProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'>, ControlAppearance {
  /** Reserve space for an icon or suffix rendered by the composite. */
  leading?: boolean
  trailing?: boolean
}

export const InputControl = forwardRef<HTMLInputElement, InputControlProps>(function InputControl(
  { controlSize = 'md', shape = 'control', variant = 'default', leading, trailing, className, style, type = 'text', ...rest }, ref,
) {
  return <input suppressHydrationWarning ref={ref} type={type} {...rest} data-ui-form-control="input" data-ui-form-size={controlSize} data-ui-form-leading-icon={leading || undefined} data-ui-form-trailing-icon={trailing || undefined} style={trailing ? { ...suffixInset, ...style } : style} className={cn(
    controlClass({ controlSize, shape, variant }, isInvalid(rest['aria-invalid'])),
    heights[controlSize], '[@media(pointer:coarse)]:min-h-target', variant === 'default' && 'read-only:bg-surface-2', leading ? 'ps-10' : 'ps-3.5', trailing ? 'pe-14' : 'pe-3.5', className,
  )} />
})

export interface SelectControlProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'>, ControlAppearance {
  /** Optional selected-option marker. className controls the wrapper's layout. */
  leadingDot?: string
  leadingIcon?: IconType
}

export const SelectControl = forwardRef<HTMLSelectElement, SelectControlProps>(function SelectControl(
  { controlSize = 'md', shape = 'control', variant = 'default', leadingDot, leadingIcon: Icon, className, children, ...rest }, ref,
) {
  return <div className={cn('relative min-w-0', className)}>
    {Icon ? <Icon aria-hidden size={16} strokeWidth={1.5} absoluteStrokeWidth className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-ink-muted" />
      : leadingDot ? <span aria-hidden className="pointer-events-none absolute start-3 top-1/2 size-2 -translate-y-1/2 rounded-full" style={{ background: leadingDot }} /> : null}
    <select suppressHydrationWarning ref={ref} {...rest} data-ui-form-control="select" data-ui-form-size={controlSize} data-ui-form-leading-icon={!!(Icon || leadingDot) || undefined} data-ui-form-trailing-icon className={cn(
      controlClass({ controlSize, shape, variant }, isInvalid(rest['aria-invalid'])),
      heights[controlSize], 'cursor-pointer appearance-none pe-10 [@media(pointer:coarse)]:min-h-target', Icon ? 'ps-10' : leadingDot ? 'ps-8' : 'ps-3.5',
    )}>{children}</select>
    <ChevronDown aria-hidden size={16} strokeWidth={1.5} absoluteStrokeWidth className="pointer-events-none absolute end-3 top-1/2 -translate-y-1/2 text-ink-muted" />
  </div>
})

export interface TextareaControlProps extends TextareaHTMLAttributes<HTMLTextAreaElement>, ControlAppearance {}

export const TextareaControl = forwardRef<HTMLTextAreaElement, TextareaControlProps>(function TextareaControl(
  { controlSize = 'md', shape = 'content', variant = 'default', className, rows = 4, ...rest }, ref,
) {
  return <textarea suppressHydrationWarning ref={ref} rows={rows} {...rest} data-ui-form-control="textarea" data-ui-form-size={controlSize} className={cn(
    controlClass({ controlSize, shape, variant }, isInvalid(rest['aria-invalid'])),
    'min-h-[80px] resize-y px-3.5 py-2.5 leading-normal', variant === 'default' && 'read-only:bg-surface-2', className,
  )} />
})
